import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

/**
 * Support-a-creator attribution.
 *
 * Creators send fans to the store with `?c=THEIRCODE`. Nothing is discounted —
 * the fan pays full price — so there is no Shopify discount code on the order to
 * read the attribution back off. Instead the creator's code is remembered in the
 * browser and written onto the cart as an order attribute, which lands on the
 * order in the admin under "Additional details" and is what the monthly
 * commission run counts.
 *
 * Two ways in, one destination:
 *   - the `?c=` link, captured on whatever page the fan lands on and kept for 30 days
 *   - the field on the cart, for a fan who saw the code in a video and typed the URL
 *
 * The typed code wins over a remembered link, because it is the more deliberate act.
 */

const STORAGE_KEY = 'skin2life:creator-code';
const LINK_PARAMS = ['c', 'creator'];
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

/** Codes are uppercased on the way in, so LARS, lars and Lars all pay the same creator. */
const CODE_PATTERN = /^[A-Z0-9][A-Z0-9_-]{1,31}$/;

const settings = () => ({
  attribute: 'Creator code',
  cartItemCount: 0,
  current: '',
  ...(window.CreatorProgram ?? {}),
});

/**
 * The attribute value the cart already carried when this page rendered. Tracked so
 * a page view with nothing to change does not spend a request on the cart.
 * @type {string}
 */
let synced = settings().current;

/**
 * @param {unknown} raw
 * @returns {string | null} The normalised code, or null if it is not one.
 */
function normalize(raw) {
  const code = String(raw ?? '')
    .trim()
    .toUpperCase();

  return CODE_PATTERN.test(code) ? code : null;
}

/**
 * localStorage throws outright in some privacy modes rather than no-opping, and
 * attribution is never worth taking the page down over.
 * @returns {string | null}
 */
function readStored() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const { code, expires } = JSON.parse(raw);
    if (typeof expires !== 'number' || Date.now() > expires) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return normalize(code);
  } catch {
    return null;
  }
}

/** @param {string} code */
function store(code) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ code, expires: Date.now() + TTL_MS }));
  } catch {
    // Attribution degrades to this visit only, which is better than throwing.
  }
}

function forget() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // As above.
  }
}

/**
 * Reads a creator code off the landing URL and remembers it.
 * @returns {string | null}
 */
function captureFromLink() {
  const params = new URLSearchParams(window.location.search);

  for (const param of LINK_PARAMS) {
    if (!params.has(param)) continue;

    const code = normalize(params.get(param));
    if (code) store(code);

    // Drop the parameter either way, so a fan who copies the URL out of the address
    // bar and posts it somewhere is not silently reassigning the credit.
    params.delete(param);
    const query = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);

    return code;
  }

  return null;
}

/**
 * Writes the code onto the cart. Passing an empty string clears the attribute.
 * @param {string} code
 * @param {{force?: boolean}} [options]
 */
async function syncToCart(code, { force = false } = {}) {
  if (!force && code === synced) return;

  const { attribute } = settings();

  try {
    const response = await fetch(
      Theme.routes.cart_update_url,
      fetchConfig('json', { body: JSON.stringify({ attributes: { [attribute]: code } }) })
    );

    if (response.ok) synced = code;
  } catch {
    // A failed sync is retried on the next cart change or page view.
  }
}

/** Puts the remembered code on the cart, if there is a cart to put it on. */
function sync() {
  const code = readStored();
  if (code) syncToCart(code);
}

captureFromLink();

// An empty cart has nothing to attribute yet, and writing to it would hand a
// cart cookie to every visitor. The first add-to-cart picks the code back up.
if (settings().cartItemCount > 0) sync();
document.addEventListener(StandardEvents.cartLinesUpdate, sync);

/**
 * The cart field, for fans who arrived without the link.
 * @extends {Component}
 */
class CreatorCodeComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#reflect(readStored());
  }

  /**
   * @param {Event} [event]
   */
  applyCode = async (event) => {
    event?.preventDefault();

    const { input } = this.refs;
    if (!(input instanceof HTMLInputElement)) return;

    const code = normalize(input.value);

    if (!code) {
      this.#setError(input.value.trim().length > 0);
      return;
    }

    this.#setError(false);
    store(code);
    this.#reflect(code);
    await syncToCart(code, { force: true });
  };

  /**
   * @param {Event} [event]
   */
  clearCode = async (event) => {
    event?.preventDefault();

    forget();
    this.#reflect(null);
    this.#setError(false);
    await syncToCart('', { force: true });
  };

  /**
   * Swaps between the entry field and the confirmation.
   * @param {string | null} code
   */
  #reflect(code) {
    const { entry, applied, appliedCode, input } = this.refs;

    if (entry instanceof HTMLElement) entry.hidden = Boolean(code);
    if (applied instanceof HTMLElement) applied.hidden = !code;
    if (appliedCode instanceof HTMLElement) appliedCode.textContent = code ?? '';
    if (input instanceof HTMLInputElement) input.value = '';
  }

  /** @param {boolean} visible */
  #setError(visible) {
    const { error, input } = this.refs;

    if (error instanceof HTMLElement) error.hidden = !visible;
    if (input instanceof HTMLInputElement) input.setAttribute('aria-invalid', String(visible));
  }
}

if (!customElements.get('creator-code')) {
  customElements.define('creator-code', CreatorCodeComponent);
}
